// src/pages/Admin/AdminProducts.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../../api/products.js';
import { categoriesAPI } from '../../api/categories.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Loader } from '../../components/ui/Loader/Loader.jsx';

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    category: '',
    sort_by: 'createdAt',
    sort_order: 'DESC',
  });
  const [total, setTotal] = useState(0);
  const { success, error } = useToast();

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.getAll({ ...filters, limit: 50 }),
        categoriesAPI.getAll({ is_active: true }),
      ]);
      setProducts(productsRes.data.data || []);
      setTotal(productsRes.data.count || 0);
      setCategories(categoriesRes.data.data || []);
    } catch (err) {
      error('Не удалось загрузить товары');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот товар?')) return;
    try {
      await productsAPI.delete(id);
      success('Товар удален');
      loadData();
    } catch (err) {
      error('Не удалось удалить товар');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await productsAPI.update(id, { is_active: !currentStatus });
      success(`Товар ${!currentStatus ? 'активирован' : 'деактивирован'}`);
      loadData();
    } catch (err) {
      error('Не удалось обновить статус');
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка товаров..." />;
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Поиск товаров..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              width: '240px',
            }}
          />
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
            }}
          >
            <option value="">Все категории</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={filters.is_active}
            onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
            }}
          >
            <option value="">Все статусы</option>
            <option value="true">Активные</option>
            <option value="false">Неактивные</option>
          </select>
        </div>
        <Link to="/admin/products/new">
          <Button>+ Добавить товар</Button>
        </Link>
      </div>

      <div style={{
        backgroundColor: 'rgb(var(--c-surface))',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            <thead>
              <tr style={{ backgroundColor: 'rgb(var(--c-bg))' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>ID</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Название</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Категория</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Цена</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Статус</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'rgb(var(--c-text-secondary))' }}>
                    Товары не найдены
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} style={{ borderTop: '1px solid rgb(var(--c-surface-2))' }}>
                    <td style={{ padding: '12px 16px', color: 'rgb(var(--c-text-secondary))', fontSize: '12px' }}>
                      {product.id.slice(0, 8)}...
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Link to={`/admin/products/${product.id}`} style={{ color: 'rgb(var(--c-text))', textDecoration: 'none' }}>
                        {product.name}
                      </Link>
                    </td>
                    <td style={{ padding: '12px 16px', color: 'rgb(var(--c-text-secondary))' }}>
                      {product.Categories?.[0]?.name || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                      {product.price ? `${product.price} ₽` : 'По запросу'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: product.is_active ? '#D4EDDA' : '#F8D7DA',
                        color: product.is_active ? '#155724' : '#721C24',
                      }}>
                        {product.is_active ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleToggleActive(product.id, product.is_active)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                          }}
                          title={product.is_active ? 'Деактивировать' : 'Активировать'}
                        >
                          {product.is_active ? '🔓' : '🔒'}
                        </button>
                        <Link to={`/admin/products/${product.id}`} style={{ textDecoration: 'none' }}>
                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '18px',
                              color: 'rgb(var(--c-accent))',
                            }}
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                        </Link>
                        <button
                          onClick={() => handleDelete(product.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: 'rgb(var(--c-danger))',
                          }}
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgb(var(--c-surface-2))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'rgb(var(--c-text-secondary))',
          fontSize: '14px',
        }}>
          <span>Всего: {total} товаров</span>
          <span>Показано: {products.length}</span>
        </div>
      </div>
    </div>
  );
};